import { useState } from "react";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const BASE_FIELDS = {
  name: { name: "name", label: "Name", type: "text", required: true, autoComplete: "name", maxLength: 100 },
  phone: { name: "phone", label: "Phone Number", type: "tel", required: true, autoComplete: "tel", maxLength: 15 },
  email: { name: "email", label: "Email (optional)", type: "email", required: false, autoComplete: "email", maxLength: 150 },
  organization: { name: "organization", label: "Business / Organisation", type: "text", required: false, autoComplete: "organization", maxLength: 200 },
  location: { name: "location", label: "Location", type: "text", required: false, maxLength: 200 },
  message: { name: "message", label: "Tell us what you need", type: "textarea", required: false, maxLength: 2000 },
};

export const LeadForm = ({
  formType,
  requirement,
  requirementOptions,
  fields = ["name", "phone", "organization", "location", "message"],
  customFields = [],
  submitLabel = "REQUEST QUOTE",
  successMessage = "Thank you. Your enquiry has been received. Our team will get in touch with you regarding your requirement.",
  trackEvent,
  testIdPrefix,
  onClose,
  sourceCta,
}) => {
  const prefix = testIdPrefix || `${formType}-form`;
  const [values, setValues] = useState({ requirement: requirement || "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const renderField = (f) => {
    const id = `${prefix}-field-${f.name}`;
    const common = {
      id,
      name: f.name,
      "data-testid": id,
      value: values[f.name] || "",
      onChange: (e) => set(f.name, e.target.value),
      required: !!f.required,
      autoComplete: f.autoComplete,
      maxLength: f.maxLength,
    };
    return (
      <div key={f.name}>
        <label htmlFor={id} className="field-label">
          {f.label} {f.required && <span className="text-cts-red" aria-hidden="true">*</span>}
        </label>
        {f.type === "textarea" ? (
          <textarea className="field-input" {...common} />
        ) : f.type === "select" ? (
          <select className="field-input" {...common}>
            <option value="">Select...</option>
            {(f.options || []).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ) : (
          <input type={f.type || "text"} inputMode={f.type === "tel" ? "tel" : f.type === "email" ? "email" : undefined} className="field-input" {...common} />
        )}
      </div>
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const details = {};
    customFields.forEach((f) => {
      if (values[f.name]) details[f.name] = values[f.name];
    });
    try {
      await api.post("/leads", {
        name: values.name || "",
        phone: values.phone || "",
        email: values.email || undefined,
        organization: values.organization || undefined,
        requirement: requirementOptions ? values.requirement : requirement || values.requirement || "General",
        location: values.location || undefined,
        message: values.message || undefined,
        form_type: formType,
        details,
        source_page: window.location.pathname,
        source_cta: sourceCta || submitLabel,
        website: values.website || "",
      });
      if (trackEvent) track(trackEvent, { form_type: formType });
      setStatus("success");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`${prefix}-success`} className="rounded-lg border border-cts-line bg-cts-soft p-6 text-center" role="status">
        <CheckCircle2 className="mx-auto mb-3 text-cts-red" size={36} strokeWidth={1.75} />
        <p className="font-body text-cts-charcoal leading-relaxed">{successMessage}</p>
        {onClose && (
          <button onClick={onClose} data-testid={`${prefix}-close-btn`} className="btn-outline mt-5 !min-h-[44px] !py-2">
            CLOSE
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid={prefix} noValidate={false} className="space-y-4">
      {/* honeypot */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`${prefix}-website`}>Website</label>
        <input id={`${prefix}-website`} type="text" tabIndex={-1} autoComplete="off"
          value={values.website || ""} onChange={(e) => set("website", e.target.value)} />
      </div>
      {fields.map((key) => renderField(BASE_FIELDS[key]))}
      {requirementOptions && renderField({
        name: "requirement", label: "Requirement", type: "select", required: true, options: requirementOptions,
      })}
      {customFields.map(renderField)}
      {status === "error" && (
        <div data-testid={`${prefix}-error`} className="flex items-start gap-2 rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-cts-red" />
          <span>{error}</span>
        </div>
      )}
      <button type="submit" data-testid={`${prefix}-submit-btn`} disabled={status === "sending"} className="btn-primary w-full disabled:opacity-60">
        {status === "sending" ? (
          <><Loader2 className="animate-spin" size={18} /> SENDING...</>
        ) : (
          submitLabel
        )}
      </button>
    </form>
  );
};
